/*
  <ck-liquido>: el fondo de la portada líquida (también lo usa el boletín).

  Capas, de abajo arriba, en un solo canvas:
    1. hondo + el mosaico de garabatos en morado, ondulando como visto a
       través del agua;
    2. el líquido: metabolas (sprites radiales sumados en un campo a 1/2 de
       resolución y cortados con un umbral suave), con sombra hondo debajo;
    3. dentro del líquido, el mismo mosaico en hondo.
  El campo se vacía detrás del texto, así que el líquido lo rodea. Del
  producto salen burbujas que suben y se unen a los ríos.

  Se pausa fuera de pantalla y con la pestaña oculta. Con «reducir
  movimiento» pinta un cuadro y no anima, salvo con `data-siempre`.
*/
(() => {
  if (customElements.get('ck-liquido')) return;

  const LIMA = [198, 255, 0], HONDO = '#2B0A66', MORADO = '#6E28E5', SOMBRA = '#1A0547';
  const Q = 2; // field resolution divisor
  const quieto = matchMedia('(prefers-reduced-motion: reduce)');

  const lienzo = (w, h) => { const c = document.createElement('canvas'); c.width = Math.max(1, w); c.height = Math.max(1, h); return c; };
  let bola = null;
  const sprite = () => {
    if (bola) return bola;
    bola = lienzo(128, 128);
    const g = bola.getContext('2d'), gr = g.createRadialGradient(64, 64, 0, 64, 64, 64);
    [[0, 1], [.25, .88], [.5, .56], [.7, .26], [.85, .08], [1, 0]].forEach(([o, a]) => gr.addColorStop(o, 'rgba(255,255,255,' + a + ')'));
    g.fillStyle = gr; g.fillRect(0, 0, 128, 128);
    return bola;
  };

  class CkLiquidoGPU {
    constructor(canvas) {
      const gl = canvas.getContext('webgl', { alpha: false, antialias: false, depth: false, stencil: false });
      if (!gl) throw new Error('WebGL no disponible');
      this.gl = gl;
      const shader = (tipo, codigo) => {
        const s = gl.createShader(tipo);
        gl.shaderSource(s, codigo); gl.compileShader(s);
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { gl.deleteShader(s); throw new Error('Shader no disponible'); }
        return s;
      };
      const precision = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT).precision ? 'highp' : 'mediump';
      this.programa = gl.createProgram();
      const vertex = shader(gl.VERTEX_SHADER, 'attribute vec2 a;varying vec2 uv;void main(){uv=(a+1.0)*0.5;gl_Position=vec4(a,0.0,1.0);}');
      const fragment = shader(gl.FRAGMENT_SHADER, `precision ${precision} float;
        varying vec2 uv;uniform sampler2D patron;uniform sampler2D campo;
        uniform vec2 tamano;uniform vec2 baldosa;uniform float sube;uniform float sombra;
        void main(){
          vec2 p=vec2(uv.x,1.0-uv.y);
          float tinta=texture2D(patron,fract((p*tamano+vec2(0.0,sube))/baldosa)).a;
          vec3 hondo=vec3(43.0,10.0,102.0)/255.0;
          vec3 fondo=mix(hondo,vec3(110.0,40.0,229.0)/255.0,tinta);
          vec2 ps=p-vec2(0.0,sombra/tamano.y);
          float os=ps.y<0.0?0.0:texture2D(campo,ps).a;
          fondo=mix(fondo,vec3(26.0,5.0,71.0)/255.0,os);
          vec3 liquido=mix(vec3(198.0,255.0,0.0)/255.0,hondo,tinta);
          gl_FragColor=vec4(mix(fondo,liquido,texture2D(campo,p).a),1.0);
        }`);
      gl.attachShader(this.programa, vertex); gl.attachShader(this.programa, fragment); gl.linkProgram(this.programa);
      gl.deleteShader(vertex); gl.deleteShader(fragment);
      if (!gl.getProgramParameter(this.programa, gl.LINK_STATUS)) throw new Error('Programa no disponible');
      gl.useProgram(this.programa);
      this.buffer = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
      const a = gl.getAttribLocation(this.programa, 'a');
      gl.enableVertexAttribArray(a); gl.vertexAttribPointer(a, 2, gl.FLOAT, false, 0, 0);
      this.uniformes = Object.fromEntries(['patron', 'campo', 'tamano', 'baldosa', 'sube', 'sombra'].map(n => [n, gl.getUniformLocation(this.programa, n)]));
      gl.uniform1i(this.uniformes.patron, 0); gl.uniform1i(this.uniformes.campo, 1);
      this.texturas = [0, 1].map(unidad => {
        const t = gl.createTexture(); gl.activeTexture(gl.TEXTURE0 + unidad); gl.bindTexture(gl.TEXTURE_2D, t);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        return t;
      });
    }

    medir(W, H, D, fw, fh, imagen, escala) {
      const gl = this.gl;
      this.fw = fw; this.fh = fh;
      gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
      gl.useProgram(this.programa);
      gl.uniform2f(this.uniformes.tamano, W, H);
      const ancho = Math.round(escala * D);
      const alto = Math.round(ancho * imagen.naturalHeight / imagen.naturalWidth);
      const tile = lienzo(ancho, alto);
      tile.getContext('2d').drawImage(imagen, 0, 0, ancho, alto);
      gl.uniform2f(this.uniformes.baldosa, ancho / D, alto / D);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, this.texturas[0]);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, tile);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, this.texturas[1]);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, fw, fh, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    }

    pinta(datos, sube, escala) {
      const gl = this.gl;
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, this.texturas[1]);
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, this.fw, this.fh, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(datos.buffer, datos.byteOffset, datos.byteLength));
      gl.uniform1f(this.uniformes.sube, sube); gl.uniform1f(this.uniformes.sombra, 10 * escala);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }

    destruir() {
      const gl = this.gl;
      this.texturas?.forEach(t => gl.deleteTexture(t));
      if (this.buffer) gl.deleteBuffer(this.buffer);
      if (this.programa) gl.deleteProgram(this.programa);
    }
  }


  class CkLiquido extends HTMLElement {
    connectedCallback() {
      this.cv = this.querySelector('.ck-liq__lienzo');
      if (!this.cv || !this.cv.getContext) return;
      this.liquido = parseFloat(this.dataset.liquido) || 1;
      this.velocidad = parseFloat(this.dataset.velocidad) || 1;
      this.siempre = this.hasAttribute('data-siempre');
      this.t = 0; this.visible = false; this.raf = 0;
      this.semilla = 9;
      this.tap = { x: 0, y: 0, last: -1e9 };
      this.rios = []; this.gotas = []; this.burbujas = []; this.siguienteBurbuja = 0;

      // No se carga la imagen ni se simula hasta que el líquido está a menos
      // de 600px de la pantalla: el del boletín, al fondo de la página, no
      // compite con lo que el visitante ve primero.
      this.io0 = new IntersectionObserver(([en]) => {
        if (!en.isIntersecting) return;
        this.io0.disconnect();
        const img = new Image();
        img.decoding = 'async'; img.crossOrigin = 'anonymous';
        img.onload = () => { if (!this.isConnected) return; this.img = img; this.prepara(); this.medir(); this.listo(); };
        img.src = this.dataset.patron;
      }, { rootMargin: '600px 0px' });
      this.io0.observe(this);

      this.onMove = (e) => { const [x, y] = this.pos(e); this.tap.x = x; this.tap.y = y; this.tap.last = performance.now(); };
      this.onDown = (e) => {
        if (e.target.closest('a,button')) return;
        const [x, y] = this.pos(e); this.tap.x = x; this.tap.y = y; this.tap.last = performance.now();
        for (let i = 0; i < 7; i++) { const a = i / 7 * 6.283 + this.r(); this.gotas.push({ x, y, vx: Math.cos(a) * 3.2, vy: Math.sin(a) * 3.2, r0: (22 + this.r() * 18) * this.s, age: 0, life: 1.6 + this.r() }); }
      };
      this.addEventListener('pointermove', this.onMove, { passive: true });
      this.addEventListener('pointerdown', this.onDown, { passive: true });

      this.ro = new ResizeObserver(() => { clearTimeout(this.rz); this.rz = setTimeout(() => { if (this.img) { this.medir(); if (!this.raf) this.cuadro(0); } }, 120); });
      this.ro.observe(this);
      this.io = new IntersectionObserver(([en]) => { this.visible = en.isIntersecting; this.sync(); });
      this.io.observe(this);
      this.onVis = () => this.sync();
      document.addEventListener('visibilitychange', this.onVis);
      this.onQuieto = () => { this.sync(); if (quieto.matches && this.img) this.cuadro(0); };
      quieto.addEventListener('change', this.onQuieto);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.raf); this.raf = 0;
      this.ro?.disconnect(); this.io?.disconnect(); this.io0?.disconnect();
      document.removeEventListener('visibilitychange', this.onVis);
      this.gpu?.destruir();
      this.cv?.removeEventListener('webglcontextlost', this.onContextLost);
      quieto.removeEventListener('change', this.onQuieto);
    }

    prepara() {
      try { this.gpu = new CkLiquidoGPU(this.cv); } catch (e) { this.fallaGPU(); return; }
      this.dataset.ckRender = 'webgl';
      this.onContextLost = (e) => { e.preventDefault(); this.fallaGPU(); };
      this.cv.addEventListener('webglcontextlost', this.onContextLost);
    }

    fallaGPU() {
      cancelAnimationFrame(this.raf); this.raf = 0;
      this.cv.removeEventListener('webglcontextlost', this.onContextLost);
      this.gpu?.destruir(); this.gpu = null;
      const nuevo = this.cv.cloneNode(false);
      this.cv.replaceWith(nuevo); this.cv = nuevo;
      this.trazo?.remove(); this.trazo = null;
      this.ctx = this.cv.getContext('2d');
      this.dataset.ckRender = '2d';
      if (this.img && this.W) { this.medir(); this.cuadro(0); this.sync(); }
    }

    r() { let a = this.semilla = this.semilla + 0x6D2B79F5 | 0; a = Math.imul(a ^ a >>> 15, 1 | a); a = a + Math.imul(a ^ a >>> 7, 61 | a) ^ a; return ((a ^ a >>> 14) >>> 0) / 4294967296; }
    pos(e) { const b = this.getBoundingClientRect(); return [e.clientX - b.left, e.clientY - b.top]; }

    // sizes, buffers, the copy's clear zone and where the product bubbles from
    medir() {
      const W = this.clientWidth, H = this.clientHeight;
      if (!W || !H) return;
      const D = Math.min(3, window.devicePixelRatio || 1);
      const cambio = W !== this.W || H !== this.H;
      this.W = W; this.H = H; this.D = D;
      this.s = Math.min(1.1, Math.max(.75, W / 1440));
      this.cv.width = Math.round(W * D); this.cv.height = Math.round(H * D);
      this.fw = Math.ceil(W / Q); this.fh = Math.ceil(H / Q);
      this.campo = lienzo(this.fw, this.fh); this.fx = this.campo.getContext('2d', { willReadFrequently: true });
      if (!this.gpu) {
        this.mascara = lienzo(this.fw, this.fh); this.mx = this.mascara.getContext('2d');
        this.tmp = lienzo(this.cv.width, this.cv.height); this.tx = this.tmp.getContext('2d');
        this.ctx.imageSmoothingQuality = 'high'; this.tx.imageSmoothingQuality = 'high';
      }
      if (this.trazo) { this.trazo.width = this.cv.width; this.trazo.height = this.cv.height; }

      const caja = this.getBoundingClientRect();
      const tb = this.querySelector('[data-liq-texto]')?.getBoundingClientRect();
      if (tb) { this.cx = tb.left - caja.left + tb.width / 2; this.cy = tb.top - caja.top + tb.height / 2; this.ex = tb.width / 2 * 1.08 + 30 * this.s; this.ey = tb.height / 2 * 1.08 + 26 * this.s;
        // on a phone the buttons carry their own fill, so only the logo and phrase need clear water
        if (W < 750) { this.ex = tb.width * .42; this.ey = tb.height * .3; this.cy -= tb.height * .2; } }
      else { this.cx = W / 2; this.cy = H / 2; this.ex = W * .25; this.ey = H * .25; }
      const pb = this.querySelector('[data-liq-producto] img')?.getBoundingClientRect();
      this.boca = pb && pb.width ? { x: pb.left - caja.left + pb.width / 2, y: pb.top - caja.top + pb.height * .06 } : null;

      const capa = this.querySelector('.ck-garabatos');
      const escala = capa ? parseFloat(getComputedStyle(capa).maskSize) : 0;
      if (this.gpu) {
        try { this.gpu.medir(W, H, D, this.fw, this.fh, this.img, escala || (W < 750 ? 240 : 360)); }
        catch (e) { this.fallaGPU(); return; }
      } else {
      const tw = Math.round((escala || (W < 750 ? 240 : 360)) * D);
      const th = Math.round(tw * (this.img.naturalHeight || 1182) / (this.img.naturalWidth || 1182));
      const tinta = (col) => {
        const c = lienzo(tw, th), g = c.getContext('2d');
        g.imageSmoothingQuality = 'high';
        g.drawImage(this.img, 0, 0, tw, th); g.globalCompositeOperation = 'source-in'; g.fillStyle = col; g.fillRect(0, 0, tw, th);
        const p = this.ctx.createPattern(c, 'repeat'); p.setTransform(new DOMMatrix().scale(1 / D)); return p;
      };
      this.patMorado = tinta(MORADO); this.patHondo = tinta(HONDO);
      }

      if (cambio) { this.semilla = 9; this.rios = []; this.gotas = []; this.burbujas = []; for (let i = 0; i < 420; i++) this.paso(1 / 60); }
    }

    listo() {
      this.cuadro(0);
      this.classList.add('esta-vivo');
      this.dispatchEvent(new Event('ck-vivo', { bubbles: true }));   // la pantalla de carga espera esto
      this.sync();
    }

    sync() {
      const corre = this.img && this.visible && !document.hidden && (this.siempre || !quieto.matches);
      if (corre && !this.raf) { this.last = 0; this.raf = requestAnimationFrame((n) => this.loop(n)); }
      if (!corre && this.raf) { cancelAnimationFrame(this.raf); this.raf = 0; }
    }

    loop(now) {
      // Si pintar un cuadro cuesta más de 12 ms (teléfonos, pantallas grandes) baja a
      // 30 cuadros por segundo: se ve igual de fluido y el equipo respira.
      if (this.mitad && (this.par = !this.par)) { this.raf = requestAnimationFrame((n) => this.loop(n)); return; }
      const dt = this.last ? Math.min(.05, (now - this.last) / 1000) : 1 / 60; this.last = now;
      const a = performance.now();
      this.cuadro(dt);
      const c = performance.now() - a;
      this.costo = this.costo ? this.costo * .9 + c * .1 : c;
      if (!this.mitad && this.costo > 12) this.mitad = true;
      this.raf = requestAnimationFrame((n) => this.loop(n));
    }

    // `alPaso(dt, t, puntero)` y `encima(ctx)` son ganchos para quien viva sobre el líquido (el avión del boletín)
    cuadro(dt) {
      if (!this.W) return;
      const p = this.paso(dt);
      if (this.alPaso) this.alPaso(dt, this.t, p);
      this.dibuja(p);
      if (this.encima) {
        if (this.gpu && !this.trazo) {
          this.trazo = this.cv.cloneNode(false); this.trazo.style.pointerEvents = 'none';
          this.cv.after(this.trazo); this.ctx = this.trazo.getContext('2d');
        }
        if (this.trazo) this.ctx.clearRect(0, 0, this.trazo.width, this.trazo.height);
        this.ctx.setTransform(this.D, 0, 0, this.D, 0, 0); this.encima(this.ctx); this.ctx.setTransform(1, 0, 0, 1, 0, 0);
      }
    }

    nuevoRio(o, y0) {
      const { W, H, s } = this;
      o.x = 40 + this.r() * (W - 80); o.y = y0 ?? H + 120 * s; o.vx = 0; o.vy = 0; o.hist = [];
      o.r0 = (58 + this.r() * 34) * s; o.ph = this.r() * 6.283; o.spd = (1.15 + this.r() * .5) * s; o.suelta = 1 + this.r() * 2; o.largo = 70 + Math.floor(this.r() * 50);
      return o;
    }

    paso(dt) {
      const { W, H, s, cx, cy, ex, ey } = this;
      dt *= this.velocidad; this.t += dt; const t = this.t, f60 = dt * 60;
      const live = performance.now() - this.tap.last < 1600;
      const px = live ? this.tap.x : cx + Math.sin(t * .29) * W * .42, py = live ? this.tap.y : H * .62 + Math.sin(t * .47 + 1) * H * .3;
      const n = Math.max(2, Math.round((W < 750 ? 9 : 11) * this.liquido * Math.max(.7, W / 1440)));
      while (this.rios.length < n) this.rios.push(this.nuevoRio({}, this.r() * H * 1.2));
      this.rios.length = n;
      for (const o of this.rios) {
        const a = -1.5708 + Math.sin(o.x * .004 + t * .35 + o.ph) * 1.1 + Math.cos(o.y * .006 - t * .3) * .6;
        let tx0 = Math.cos(a) * o.spd, ty0 = Math.sin(a) * o.spd;
        const qx = (o.x - cx) / ex, qy = (o.y - cy) / ey, q = qx * qx + qy * qy;
        if (q < 2) { const k = (2 - q) * .9 * o.spd, l = Math.sqrt(q) || 1; tx0 += qx / l * k; ty0 += qy / l * k * .4; }
        const dx = px - o.x, dy = py - o.y, d = Math.hypot(dx, dy) || 1, R = 340 * s;
        if (d < R) { const k = (1 - d / R) * (live ? 1.6 : .6) * o.spd; tx0 += dx / d * k; ty0 += dy / d * k; }
        // `repulsores` [{x, y, R}]: algo que atraviesa el líquido y lo aparta (el avión del boletín)
        if (this.repulsores) for (const q of this.repulsores) {
          const rx = o.x - q.x, ry = o.y - q.y, rd = Math.hypot(rx, ry) || 1;
          if (rd < q.R) { const k = (1 - rd / q.R) * 4 * o.spd; tx0 += rx / rd * k; ty0 += ry / rd * k; }
        }
        o.vx += (tx0 - o.vx) * .035 * f60; o.vy += (ty0 - o.vy) * .035 * f60;
        o.x += o.vx * f60; o.y += o.vy * f60;
        o.hist.unshift(o.x, o.y); if (o.hist.length > o.largo * 2) o.hist.length = o.largo * 2;
        o.suelta -= dt;
        if (o.suelta < 0) { o.suelta = 1.2 + this.r() * 2.2; this.gotas.push({ x: o.x, y: o.y, vx: o.vx * .4 + (this.r() - .5) * 1.2, vy: o.vy * .4 - this.r() * .6, r0: o.r0 * (.35 + this.r() * .2), age: 0, life: 2.5 + this.r() * 2 }); }
        if (o.y < -o.r0 * 3 - o.largo * o.spd || o.x < -220 || o.x > W + 220) this.nuevoRio(o);
      }
      for (const g of this.gotas) { g.vy -= .012 * f60; g.vx *= .99; g.x += g.vx * f60 * s; g.y += g.vy * f60 * s; g.age += dt; }
      this.gotas = this.gotas.filter(g => g.age < g.life);
      if (this.boca) {
        this.siguienteBurbuja -= dt;
        if (this.siguienteBurbuja < 0) { this.siguienteBurbuja = .22; this.burbujas.push({ x: this.boca.x + (this.r() - .5) * 6, y: this.boca.y, vy: -1.5 * s, age: 0, life: 2.6, ph: this.r() * 6.283 }); }
        for (const b of this.burbujas) { b.age += dt; b.x += Math.sin(t * 2.2 + b.ph + b.age * 2) * .45 * s * f60; b.y += b.vy * f60; }
        this.burbujas = this.burbujas.filter(b => b.age < b.life);
      }
      return { px, py, live };
    }

    // Garabatos quietos que solo suben despacio: sin ondas ni ondulación con el cursor.
    patron(g, pat, sube) {
      const { W, H, D } = this;
      g.fillStyle = pat;
      g.setTransform(D, 0, 0, D, 0, -sube * D);
      g.fillRect(0, sube, W, H);
      g.setTransform(1, 0, 0, 1, 0, 0);
    }

    dibuja({ px, py, live }) {
      const { ctx, fx, mx, tx, tmp, mascara, fw, fh, D, s, t } = this;
      const sube = t * 14 * s, spr = sprite();
      // La luz del boletín sigue la fase del mosaico del canvas. El hero no lleva luz.
      if (this.classList.contains('ck-boletin__panel')) this.style.setProperty('--ck-liq-desfase', -sube + 'px');

      if (!this.gpu) {
      ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalCompositeOperation = 'source-over'; ctx.globalAlpha = 1;
      ctx.fillStyle = HONDO; ctx.fillRect(0, 0, this.cv.width, this.cv.height);
      this.patron(ctx, this.patMorado, sube);
      }

      fx.globalCompositeOperation = 'source-over'; fx.clearRect(0, 0, fw, fh);
      fx.globalCompositeOperation = 'lighter';
      const pinta = (x, y, rad) => { const k = rad * 2 / Q; if (k > .3) fx.drawImage(spr, x / Q - k, y / Q - k, k * 2, k * 2); };
      for (const o of this.rios) {
        const m = o.hist.length / 2;
        for (let i = 0; i < m; i += 3) pinta(o.hist[i * 2], o.hist[i * 2 + 1], o.r0 * (1 - i / m * .7) * (1 + Math.sin(t * 2.6 + i * .35 + o.ph) * .1));
      }
      for (const g of this.gotas) pinta(g.x, g.y, g.r0 * Math.min(1, g.age / .3) * Math.min(1, g.life - g.age));
      for (const b of this.burbujas) pinta(b.x, b.y, (14 + 26 * b.age / b.life) * s * Math.min(1, (b.life - b.age) * 1.5));
      pinta(px, py, (live ? 62 : 44) * s);
      // `cortes` [{x, y, r}]: huecos en el campo, la estela que deja algo al cruzar el líquido
      if (this.cortes) {
        fx.globalCompositeOperation = 'destination-out';
        for (const c of this.cortes) {
          if (c.tri) {   // hueco poligonal: lo pinta el avión del boletín
            fx.fillStyle = '#000'; fx.beginPath();
            fx.moveTo(c.tri[0] / Q, c.tri[1] / Q); fx.lineTo(c.tri[2] / Q, c.tri[3] / Q); fx.lineTo(c.tri[4] / Q, c.tri[5] / Q);
            fx.closePath(); fx.fill();
            continue;
          }
          const k = c.r * 2 / Q; if (k > .3) fx.drawImage(spr, c.x / Q - k, c.y / Q - k, k * 2, k * 2);
        }
      }

      const id = fx.getImageData(0, 0, fw, fh), d = id.data;
      for (let i = 0; i < d.length; i += 4) {
        let a = (d[i + 3] / 255 - .46) / .05; a = a < 0 ? 0 : a > 1 ? 1 : a; a = a * a * (3 - 2 * a);
        d[i] = LIMA[0]; d[i + 1] = LIMA[1]; d[i + 2] = LIMA[2]; d[i + 3] = a * 255;
      }
      if (this.gpu) { this.gpu.pinta(d, sube, s); return; }
      mx.putImageData(id, 0, 0);

      tx.globalCompositeOperation = 'source-over'; tx.clearRect(0, 0, tmp.width, tmp.height);
      tx.drawImage(mascara, 0, 0, tmp.width, tmp.height);
      tx.globalCompositeOperation = 'source-in'; tx.fillStyle = SOMBRA; tx.fillRect(0, 0, tmp.width, tmp.height);
      ctx.drawImage(tmp, 0, Math.round(10 * s * D));
      tx.globalCompositeOperation = 'source-over'; tx.clearRect(0, 0, tmp.width, tmp.height);
      tx.drawImage(mascara, 0, 0, tmp.width, tmp.height);
      tx.globalCompositeOperation = 'source-atop';
      this.patron(tx, this.patHondo, sube);
      ctx.drawImage(tmp, 0, 0);
    }
  }

  customElements.define('ck-liquido', CkLiquido);
})();
